import axios from 'axios';
import { useState, useEffect } from "react";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Alert, AlertDescription, AlertTitle } from "@/components/ui/alert";
import { 
  Link2, 
  Unlink, 
  CheckCircle, 
  AlertCircle, 
  Loader2,
  Database,
  FileSpreadsheet,
  Upload,
  Calendar,
  Clock,
  RefreshCw
} from "lucide-react";
import { toast } from "sonner";
import api from "@/lib/api";

export const ConnexionQuickBooks = () => {
  const [isConnected, setIsConnected] = useState(false);
  const [tokenValid, setTokenValid] = useState(null);
  const [tokenExpired, setTokenExpired] = useState(false);
  const [statusMessage, setStatusMessage] = useState('');
  const [realmId, setRealmId] = useState(null);
  const [connectedAt, setConnectedAt] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [isSyncing, setIsSyncing] = useState(false);
  const [syncType, setSyncType] = useState(null); // 'full' ou 'monthly'
  const [syncResult, setSyncResult] = useState(null);
  const [syncStatus, setSyncStatus] = useState(null);
  const [uploading, setUploading] = useState(null); // year being uploaded
  const [uploadStatus, setUploadStatus] = useState({});

  // Vérifier le statut de connexion au chargement
  useEffect(() => {
    checkConnectionStatus();
    loadSyncStatus();
    // Charger le redirect_uri pour l'afficher
    axios.get(`${api.baseUrl}/quickbooks/auth-url`).then(r => r.data).then(d => {
      if (d.redirect_uri) setRedirectUri(d.redirect_uri);
    }).catch(() => {});
    
    // Vérifier les paramètres URL (callback OAuth)
    const params = new URLSearchParams(window.location.search);
    const qbSuccess = params.get('qb_success');
    const qbError = params.get('qb_error');
    const qbRealmId = params.get('realm_id');
    
    if (qbSuccess === 'true') {
      toast.success("QuickBooks connecté avec succès!");
      setIsConnected(true);
      setRealmId(qbRealmId);
      // Nettoyer l'URL
      window.history.replaceState({}, document.title, '/quickbooks');
    } else if (qbError) {
      toast.error(`Erreur de connexion: ${qbError}`);
      window.history.replaceState({}, document.title, '/quickbooks');
    }
  }, []);

  const loadSyncStatus = async () => {
    try {
      const response = await axios.get(`${api.baseUrl}/quickbooks/sync-status`);
      const data = response.data;
      setSyncStatus(data);
    } catch (error) {
      console.error("Erreur chargement sync status:", error);
    }
  };

  const checkConnectionStatus = async () => {
    try {
      setIsLoading(true);
      const response = await axios.get(`${api.baseUrl}/quickbooks/status`);
      const data = response.data;
      setIsConnected(data.connected);
      setTokenValid(data.token_valid);
      setTokenExpired(data.token_expired || false);
      setStatusMessage(data.message || '');
      setRealmId(data.realm_id);
      setConnectedAt(data.connected_at);
    } catch (error) {
      console.error("Erreur vérification status:", error);
    } finally {
      setIsLoading(false);
    }
  };

  const [redirectUri, setRedirectUri] = useState('');

  const handleConnect = async () => {
    try {
      setIsLoading(true);
      const response = await axios.get(`${api.baseUrl}/quickbooks/auth-url`);
      const data = response.data;
      
      if (data.redirect_uri) {
        setRedirectUri(data.redirect_uri);
      }
      
      if (data.auth_url) {
        window.location.href = data.auth_url;
      } else {
        toast.error("Impossible de generer l'URL d'autorisation");
      }
    } catch (error) {
      console.error("Erreur connexion:", error);
      toast.error("Erreur lors de la connexion a QuickBooks");
      setIsLoading(false);
    }
  };

  const handleDisconnect = async () => {
    try {
      setIsLoading(true);
      const response = await axios.post(`${api.baseUrl}/quickbooks/disconnect`);
      
      if (response.status === 200) {
        setIsConnected(false);
        setRealmId(null);
        setConnectedAt(null);
        setSyncResult(null);
        toast.success("QuickBooks déconnecté");
      }
    } catch (error) {
      console.error("Erreur déconnexion:", error);
      toast.error("Erreur lors de la déconnexion");
    } finally {
      setIsLoading(false);
    }
  };

  const handleSync = async (type) => {
    try {
      setIsSyncing(true);
      setSyncType(type);
      setSyncResult(null);
      
      const response = await axios.post(`${api.baseUrl}/quickbooks/sync?sync_type=${type}`);
      
      const data = response.data;
      
      if (response.status === 200) {
        setSyncResult(data);
        setSyncStatus(prev => ({
          ...prev,
          last_sync: new Date().toISOString(),
          entries_synced: data.journal_entries_count,
          journal_transactions_synced: data.journal_transactions_count,
          sync_type: type
        }));
        toast.success(data.message);
      } else {
        toast.error(data.detail || "Erreur lors de la synchronisation");
      }
    } catch (error) {
      console.error("Erreur sync:", error);
      toast.error("Erreur lors de la synchronisation");
    } finally {
      setIsSyncing(false);
      setSyncType(null);
    }
  };

  const handleFileUpload = async (e, year) => {
    const file = e.target.files[0];
    if (!file) return;
    setUploading(year);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const response = await axios.post(`${api.baseUrl}/journal/upload?year=${year}`, fd, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });
      const data = response.data;
      if (response.status === 200) {
        setUploadStatus(prev => ({ ...prev, [year]: data }));
        toast.success(`${data.transactions_imported || 0} transactions importées pour ${year}`);
      } else {
        toast.error(data.detail || "Erreur lors de l'import");
      }
    } catch (error) {
      toast.error("Erreur lors de l'upload du fichier");
    } finally {
      setUploading(null);
      e.target.value = "";
    }
  };

  if (isLoading && !isConnected) {
    return (
      <div className="flex items-center justify-center h-64">
        <Loader2 className="w-8 h-8 animate-spin text-emerald-600" />
      </div>
    );
  }

  return (
    <div data-testid="quickbooks-page" className="space-y-6">
      {/* Header */}
      <div className="animate-fade-in">
        <h1 className="font-heading text-3xl font-bold text-slate-900 flex items-center gap-3">
          <Database className="w-8 h-8 text-emerald-600" />
          Connexion QuickBooks
        </h1>
        <p className="text-slate-500 mt-1">
          Synchronisez automatiquement vos données financières depuis QuickBooks Online
        </p>
      </div>

      {/* Statut de connexion */}
      <Card className="animate-fade-in delay-100">
        <CardHeader>
          <div className="flex items-center justify-between">
            <div>
              <CardTitle className="text-lg">Statut de la connexion</CardTitle>
              <CardDescription>
                {isConnected && tokenValid
                  ? "Votre compte QuickBooks est connecte et fonctionnel" 
                  : tokenExpired
                  ? "Votre token QuickBooks a expire — reconnexion necessaire"
                  : "Connectez votre compte QuickBooks pour synchroniser vos donnees"
                }
              </CardDescription>
            </div>
            <Badge 
              variant={isConnected && tokenValid ? "default" : "secondary"}
              className={isConnected && tokenValid ? "bg-emerald-600" : tokenExpired ? "bg-amber-500" : ""}
            >
              {isConnected && tokenValid ? (
                <><CheckCircle className="w-3 h-3 mr-1" /> Connecte</>
              ) : tokenExpired ? (
                <><AlertCircle className="w-3 h-3 mr-1" /> Token expire</>
              ) : (
                <><AlertCircle className="w-3 h-3 mr-1" /> Non connecte</>
              )}
            </Badge>
          </div>
        </CardHeader>
        <CardContent>
          {/* État 1: Connecté et fonctionnel */}
          {isConnected && tokenValid ? (
            <div className="space-y-4">
              <div className="bg-emerald-50 border border-emerald-200 rounded-lg p-4">
                <div className="flex items-center gap-4">
                  <CheckCircle className="w-8 h-8 text-emerald-600" />
                  <div>
                    <p className="font-medium text-emerald-900">QuickBooks Online connecte</p>
                    <p className="text-sm text-emerald-700">Company ID: {realmId}</p>
                    {connectedAt && <p className="text-xs text-emerald-600 mt-1">Connecte le: {new Date(connectedAt).toLocaleDateString('fr-CA')}</p>}
                  </div>
                </div>
              </div>
              
              <div className="flex flex-wrap gap-3">
                <Button onClick={() => handleSync('full')} disabled={isSyncing} className="flex items-center gap-2 bg-blue-600 hover:bg-blue-700">
                  {isSyncing && syncType === 'full' ? <Loader2 className="w-4 h-4 animate-spin" /> : <Calendar className="w-4 h-4" />}
                  {isSyncing && syncType === 'full' ? "Import en cours..." : "Import complet (2024-2025)"}
                </Button>
                <Button onClick={() => handleSync('monthly')} disabled={isSyncing} className="flex items-center gap-2 bg-emerald-600 hover:bg-emerald-700">
                  {isSyncing && syncType === 'monthly' ? <Loader2 className="w-4 h-4 animate-spin" /> : <Clock className="w-4 h-4" />}
                  {isSyncing && syncType === 'monthly' ? "Sync en cours..." : "Sync mois en cours"}
                </Button>
                <Button onClick={() => handleSync('yearly')} disabled={isSyncing} className="flex items-center gap-2 bg-purple-600 hover:bg-purple-700" data-testid="btn-sync-yearly">
                  {isSyncing && syncType === 'yearly' ? <Loader2 className="w-4 h-4 animate-spin" /> : <Calendar className="w-4 h-4" />}
                  {isSyncing && syncType === 'yearly' ? "Sync en cours..." : `Sync annee ${new Date().getFullYear()}`}
                </Button>
                <Button variant="outline" onClick={handleDisconnect} disabled={isLoading} className="flex items-center gap-2 text-red-600 hover:text-red-700 hover:bg-red-50">
                  <Unlink className="w-4 h-4" /> Deconnecter
                </Button>
              </div>
              
              <div className="mt-4 grid grid-cols-1 md:grid-cols-3 gap-3">
                <div className="bg-blue-50 border border-blue-200 rounded-lg p-3">
                  <p className="text-sm font-medium text-blue-900">Import complet</p>
                  <p className="text-xs text-blue-700">Toutes les ecritures depuis janvier 2024. Pour la premiere synchronisation.</p>
                </div>
                <div className="bg-emerald-50 border border-emerald-200 rounded-lg p-3">
                  <p className="text-sm font-medium text-emerald-900">Sync mensuelle</p>
                  <p className="text-xs text-emerald-700">Ecritures du mois en cours seulement. A utiliser regulierement.</p>
                </div>
                <div className="bg-purple-50 border border-purple-200 rounded-lg p-3">
                  <p className="text-sm font-medium text-purple-900">Sync annee en cours</p>
                  <p className="text-xs text-purple-700">Toutes les ecritures de {new Date().getFullYear()} (janvier a aujourd'hui).</p>
                </div>
              </div>
            </div>

          ) : tokenExpired ? (
            /* État 2: Token expiré — reconnexion nécessaire */
            <div className="space-y-4">
              <div className="bg-amber-50 border border-amber-200 rounded-lg p-4">
                <div className="flex items-center gap-4">
                  <AlertCircle className="w-8 h-8 text-amber-600 shrink-0" />
                  <div>
                    <p className="font-medium text-amber-900">Reconnexion necessaire</p>
                    <p className="text-sm text-amber-700 mt-1">Le token d'acces a QuickBooks a expire. Cliquez sur le bouton ci-dessous pour vous reconnecter.</p>
                    {connectedAt && <p className="text-xs text-amber-600 mt-2">Derniere connexion : {new Date(connectedAt).toLocaleDateString('fr-CA')} | Company ID: {realmId}</p>}
                  </div>
                </div>
              </div>
              <Button onClick={handleConnect} disabled={isLoading} size="lg" className="bg-[#2CA01C] hover:bg-[#248a17]">
                {isLoading ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <RefreshCw className="w-4 h-4 mr-2" />}
                Reconnecter QuickBooks
              </Button>
            </div>

          ) : (
            /* État 3: Non connecté */
            <div className="space-y-4">
              <Alert>
                <AlertCircle className="h-4 w-4" />
                <AlertTitle>Connexion requise</AlertTitle>
                <AlertDescription>Cliquez sur le bouton ci-dessous pour autoriser l'acces a vos donnees QuickBooks.</AlertDescription>
              </Alert>
              <Button onClick={handleConnect} disabled={isLoading} size="lg" className="flex items-center gap-2 bg-[#2CA01C] hover:bg-[#248a17]">
                {isLoading ? <Loader2 className="w-4 h-4 animate-spin" /> : <Link2 className="w-4 h-4" />}
                Connecter a QuickBooks
              </Button>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Résultat de synchronisation */}
      {(syncResult || syncStatus?.last_sync) && (
        <Card className="animate-fade-in border-emerald-200 bg-emerald-50/30">
          <CardHeader>
            <CardTitle className="text-lg flex items-center gap-2">
              <CheckCircle className="w-5 h-5 text-emerald-600" />
              Dernière synchronisation
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <div className="bg-white rounded-lg p-4 text-center">
                <FileSpreadsheet className="w-8 h-8 text-blue-600 mx-auto mb-2" />
                <p className="text-2xl font-bold text-slate-900">
                  {syncResult?.journal_entries_count || syncStatus?.entries_synced || 0}
                </p>
                <p className="text-sm text-slate-500">Écritures de journal</p>
              </div>
              <div className="bg-white rounded-lg p-4 text-center">
                <Calendar className="w-8 h-8 text-amber-600 mx-auto mb-2" />
                <p className="text-lg font-bold text-slate-900">
                  {syncResult?.date_range 
                    ? `${syncResult.date_range.start} → ${syncResult.date_range.end}`
                    : syncStatus?.date_range
                      ? `${syncStatus.date_range.start} → ${syncStatus.date_range.end}`
                      : '-'
                  }
                </p>
                <p className="text-sm text-slate-500">Période importée</p>
              </div>
              <div className="bg-white rounded-lg p-4 text-center">
                <Clock className="w-8 h-8 text-emerald-600 mx-auto mb-2" />
                <p className="text-lg font-bold text-slate-900">
                  {syncResult?.sync_type === 'full' || syncStatus?.sync_type === 'full' 
                    ? 'Complet' 
                    : 'Mensuel'}
                </p>
                <p className="text-sm text-slate-500">Type de sync</p>
              </div>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Upload Journal Excel */}
      <Card className="animate-fade-in delay-200 border-blue-200">
        <CardHeader>
          <CardTitle className="text-lg flex items-center gap-2">
            <FileSpreadsheet className="w-5 h-5 text-blue-600" />
            Importer Journal Excel
          </CardTitle>
          <CardDescription>
            Uploadez vos fichiers Journal exportés depuis QuickBooks (format .xlsx)
          </CardDescription>
        </CardHeader>
        <CardContent>
          <div className="space-y-4">
            {/* Upload 2024 */}
            <div className="border border-dashed border-slate-300 rounded-lg p-4 hover:border-blue-400 transition-colors">
              <div className="flex items-center justify-between">
                <div>
                  <p className="font-medium text-slate-900">Journal 2024</p>
                  <p className="text-sm text-slate-500">01-01-2024 au 31-12-2024</p>
                </div>
                <div className="flex items-center gap-2">
                  <input
                    type="file"
                    accept=".xlsx,.xls"
                    id="file-2024"
                    className="hidden"
                    onChange={(e) => handleFileUpload(e, 2024)}
                  />
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => document.getElementById('file-2024').click()}
                    disabled={uploading === 2024}
                    className="flex items-center gap-2"
                  >
                    {uploading === 2024 ? (
                      <Loader2 className="w-4 h-4 animate-spin" />
                    ) : (
                      <Upload className="w-4 h-4" />
                    )}
                    {uploading === 2024 ? "Import en cours..." : "Choisir fichier"}
                  </Button>
                  {uploadStatus[2024] && (
                    <Badge variant="default" className="bg-emerald-600">
                      {uploadStatus[2024].transactions_imported} transactions
                    </Badge>
                  )}
                </div>
              </div>
            </div>

            {/* Upload 2025 */}
            <div className="border border-dashed border-slate-300 rounded-lg p-4 hover:border-blue-400 transition-colors">
              <div className="flex items-center justify-between">
                <div>
                  <p className="font-medium text-slate-900">Journal 2025</p>
                  <p className="text-sm text-slate-500">01-01-2025 à aujourd&apos;hui</p>
                </div>
                <div className="flex items-center gap-2">
                  <input
                    type="file"
                    accept=".xlsx,.xls"
                    id="file-2025"
                    className="hidden"
                    onChange={(e) => handleFileUpload(e, 2025)}
                  />
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => document.getElementById('file-2025').click()}
                    disabled={uploading === 2025}
                    className="flex items-center gap-2"
                  >
                    {uploading === 2025 ? (
                      <Loader2 className="w-4 h-4 animate-spin" />
                    ) : (
                      <Upload className="w-4 h-4" />
                    )}
                    {uploading === 2025 ? "Import en cours..." : "Choisir fichier"}
                  </Button>
                  {uploadStatus[2025] && (
                    <Badge variant="default" className="bg-emerald-600">
                      {uploadStatus[2025].transactions_imported} transactions
                    </Badge>
                  )}
                </div>
              </div>
            </div>

            {/* Instructions */}
            <Alert>
              <FileSpreadsheet className="h-4 w-4" />
              <AlertTitle>Comment exporter depuis QuickBooks?</AlertTitle>
              <AlertDescription className="text-sm">
                <ol className="list-decimal ml-4 mt-2 space-y-1">
                  <li>Dans QuickBooks, allez dans <strong>Rapports → Journal</strong></li>
                  <li>Sélectionnez la période (ex: 01-01-2024 au 31-12-2024)</li>
                  <li>Cliquez sur <strong>Exporter → Excel</strong></li>
                  <li>Uploadez le fichier .xlsx ici</li>
                </ol>
              </AlertDescription>
            </Alert>
          </div>
        </CardContent>
      </Card>

      {/* Instructions */}
      <Card className="animate-fade-in delay-300">
        <CardHeader>
          <CardTitle className="text-lg">Comment ça fonctionne?</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="space-y-4">
            <div className="flex items-start gap-4">
              <div className="w-8 h-8 bg-emerald-100 rounded-full flex items-center justify-center text-emerald-600 font-bold flex-shrink-0">1</div>
              <div>
                <p className="font-medium text-slate-900">Connectez votre compte</p>
                <p className="text-sm text-slate-500">Cliquez sur &quot;Connecter à QuickBooks&quot; et autorisez l&apos;accès à vos données.</p>
              </div>
            </div>
            <div className="flex items-start gap-4">
              <div className="w-8 h-8 bg-blue-100 rounded-full flex items-center justify-center text-blue-600 font-bold flex-shrink-0">2</div>
              <div>
                <p className="font-medium text-slate-900">Importez vos Journaux Excel</p>
                <p className="text-sm text-slate-500">Uploadez vos fichiers Journal 2024 et 2025 exportés depuis QuickBooks.</p>
              </div>
            </div>
            <div className="flex items-start gap-4">
              <div className="w-8 h-8 bg-emerald-100 rounded-full flex items-center justify-center text-emerald-600 font-bold flex-shrink-0">3</div>
              <div>
                <p className="font-medium text-slate-900">Analysez vos finances</p>
                <p className="text-sm text-slate-500">Vos données seront automatiquement intégrées dans tous les rapports et tableaux de bord.</p>
              </div>
            </div>
          </div>
        </CardContent>
      </Card>
    </div>
  );
};

export default ConnexionQuickBooks;
